/**
 * The runtime half of the phase-0 asset pipeline: turn a newly-installed service
 * worker into a user-visible, controlled reload.
 *
 * A production build keys the SW precache to the single build hash
 * (`vite.config.ts` → Workbox `cacheId: 4f-<BUILD_HASH>`). A new deploy ⇒ new
 * hash ⇒ new precache: the SW installs in the background and, once waiting,
 * fires `onNeedRefresh`. This controller then surfaces a sticky "update
 * available" `Toast` with a Reload action; accepting calls the updater with
 * `true`, which `skipWaiting()`s the waiting worker and reloads onto the fresh,
 * fingerprinted assets — no stale-cache lock-in.
 *
 * Pure + framework-agnostic (no DOM, no virtual module): the SW registrar and
 * the toast sink are injected (DIP), so it is fully unit-testable and the
 * concrete `virtual:pwa-register` adapter (`./register.ts`) stays isolated.
 */
import type { ToastStore } from '@4f/ui';

/** Localised copy for the update prompt — injected from i18n, never a literal. */
export interface UpdateMessages {
  /** Toast title, e.g. "Update available". */
  readonly available: string;
  /** Toast body, e.g. "A new version is ready." */
  readonly body: string;
  /** Action-button label, e.g. "Reload". */
  readonly reload: string;
  /** Failure toast body when activating/reloading the new worker rejects. */
  readonly failed: string;
}

/** Callbacks the SW registration invokes as its lifecycle advances. */
export interface SwUpdateHandlers {
  /** A new build's SW installed and is waiting to activate — prompt the user. */
  readonly onNeedRefresh: () => void;
  /** The app shell is cached and ready to work offline (optional hook). */
  readonly onOfflineReady?: () => void;
}

/**
 * Activates the waiting SW and reloads the page when called with `true`. Wraps
 * `vite-plugin-pwa`'s `registerSW` return value.
 */
export type SwUpdater = (reload: boolean) => Promise<void>;

/**
 * Registers SW update detection and returns the updater. Injected so production
 * uses the `virtual:pwa-register` adapter and tests pass a fake (DIP/OCP).
 */
export type SwRegister = (handlers: SwUpdateHandlers) => SwUpdater;

/** Everything the controller needs — all injected at the composition root. */
export interface ServiceWorkerUpdateDeps {
  /** Registrar for SW updates (the virtual-module adapter in production). */
  readonly register: SwRegister;
  /** Toast sink; only `add` is used, so any structural match works in tests. */
  readonly toasts: Pick<ToastStore, 'add'>;
  /** Localised prompt copy. */
  readonly messages: UpdateMessages;
}

/**
 * Wire the update flow. Returns the {@link SwUpdater} so a caller can also force
 * an update programmatically. Registers exactly once; each `onNeedRefresh`
 * enqueues one sticky prompt whose Reload action applies the update.
 */
export function initServiceWorkerUpdates(deps: ServiceWorkerUpdateDeps): SwUpdater {
  // Forward reference: the prompt's Reload handler needs the updater, which is
  // only returned after `register()`. `onNeedRefresh` fires later (post-install),
  // never synchronously during registration, so the binding is always set by then.
  let updateSW: SwUpdater | undefined;

  const promptUpdate = (): void => {
    deps.toasts.add({
      title: deps.messages.available,
      message: deps.messages.body,
      variant: 'info',
      duration: 0, // sticky — the user decides when to reload
      action: {
        label: deps.messages.reload,
        onClick: () => {
          // Activating the waiting worker + reloading can reject (e.g. the SW
          // controller went away). Catch it so it is never an unhandled
          // rejection, and surface localized failure feedback so the user can
          // retry instead of being stranded on a half-applied update.
          void updateSW?.(true)?.catch(() => {
            deps.toasts.add({ message: deps.messages.failed, variant: 'danger' });
          });
        },
      },
    });
  };

  updateSW = deps.register({ onNeedRefresh: promptUpdate });
  return updateSW;
}
